if (!customElements.get('finch-post-add-offer')) {
  customElements.define(
    'finch-post-add-offer',
    class FinchPostAddOffer extends HTMLElement {
      constructor() {
        super();

        this.dialog = this.querySelector('[data-finch-post-add-offer-dialog]');
        this.statusElement = this.querySelector('[data-finch-offer-status]');
        this.offerButtons = Array.from(this.querySelectorAll('[data-finch-offer-add]'));
        this.addedVariantIds = new Set();
        this.availableOfferCount = this.offerButtons.length;
        this.requestInFlight = false;
        this.cartDrawer = null;
        this.triggerElement = null;
        this.latestCartData = null;
        this.drawerOpened = false;
        this.finishInProgress = false;
        this.offerRequestTimeout = null;

        this.onClick = this.onClick.bind(this);
        this.onCancel = this.onCancel.bind(this);
        this.onDialogClose = this.onDialogClose.bind(this);
      }

      connectedCallback() {
        if (!this.dialog || this.dataset.finchPostAddOfferReady === 'true') return;

        this.dataset.finchPostAddOfferReady = 'true';
        this.addEventListener('click', this.onClick);
        this.dialog.addEventListener('cancel', this.onCancel);
        this.dialog.addEventListener('close', this.onDialogClose);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.dialog?.removeEventListener('cancel', this.onCancel);
        this.dialog?.removeEventListener('close', this.onDialogClose);
        if (this.offerRequestTimeout) window.clearTimeout(this.offerRequestTimeout);
        document.documentElement.classList.remove('finch-post-add-offer-open');
      }

      canOpen({ productForm } = {}) {
        return Boolean(
          this.dialog &&
            !this.dialog.open &&
            !this.requestInFlight &&
            this.availableOfferCount > 0 &&
            productForm?.matches?.('[data-finch-main-product-form]') &&
            document.querySelector('cart-drawer')
        );
      }

      open({ cartData: response, cartDrawer, triggerElement, productForm } = {}) {
        if (!this.canOpen({ productForm })) return false;

        this.cartDrawer = cartDrawer || document.querySelector('cart-drawer');
        if (!this.cartDrawer) return false;

        this.latestCartData = response;
        this.triggerElement = triggerElement || document.activeElement;
        this.addedVariantIds = new Set();
        this.availableOfferCount = this.offerButtons.length;
        this.requestInFlight = false;
        this.drawerOpened = false;
        this.finishInProgress = false;
        this.resetOfferButtons();
        this.setStatus('');
        this.setBusy(false);

        this.cartDrawer.setActiveElement?.(this.triggerElement);
        document.documentElement.classList.add('finch-post-add-offer-open');
        this.dialog.showModal();

        window.requestAnimationFrame(() => {
          this.offerButtons.find((button) => !button.disabled)?.focus();
        });

        return true;
      }

      resetOfferButtons() {
        this.offerButtons.forEach((button) => {
          button.classList.remove('is-added');
          button.removeAttribute('data-finch-offer-added');
          button.disabled = false;
          button.setAttribute('aria-disabled', 'false');
          button.setAttribute('aria-busy', 'false');
          const label = button.querySelector('[data-finch-offer-add-label]');
          if (label) label.textContent = button.dataset.defaultLabel || label.textContent;
          if (!button.dataset.defaultLabel && label) button.dataset.defaultLabel = label.textContent;
        });
      }

      onClick(event) {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;

        const addButton = target.closest('[data-finch-offer-add]');
        if (addButton) {
          event.preventDefault();
          this.addOffer(addButton);
          return;
        }

        if (target.closest('[data-finch-offer-close], [data-finch-offer-continue]')) {
          event.preventDefault();
          this.finish();
          return;
        }

        if (event.target === this.dialog) {
          this.finish();
        }
      }

      onCancel(event) {
        event.preventDefault();
        this.finish();
      }

      onDialogClose() {
        document.documentElement.classList.remove('finch-post-add-offer-open');

        if (!this.finishInProgress) this.complete();
        this.finishInProgress = false;
      }

      finish() {
        if (!this.dialog?.open) return;

        this.finishInProgress = true;
        this.dialog.close();
        this.complete();
      }

      complete() {
        if (this.drawerOpened || !this.cartDrawer) return;

        this.drawerOpened = true;
        document.documentElement.classList.remove('finch-post-add-offer-open');
        window.requestAnimationFrame(() => this.cartDrawer.open(this.triggerElement));
      }

      async addOffer(button) {
        if (this.requestInFlight || button.disabled) return;

        const variantId = Number(button.dataset.variantId);
        if (!Number.isFinite(variantId) || variantId <= 0 || this.addedVariantIds.has(variantId)) return;

        if (
          typeof this.cartDrawer?.enqueueCartMutation !== 'function' ||
          typeof this.cartDrawer?.requestJSON !== 'function'
        ) {
          this.setStatus(this.dataset.errorText, true);
          return;
        }

        this.setBusy(true, button);
        this.setStatus(this.dataset.loadingLabel);
        this.offerRequestTimeout = window.setTimeout(() => {
          if (this.dialog?.open) this.finish();
        }, 20000);

        try {
          const parsedState = await this.cartDrawer.enqueueCartMutation(async () => {
            const cartData = await this.cartDrawer.requestJSON(window.routes.cart_add_url, {
              items: [{ id: variantId, quantity: 1 }],
              sections: this.cartDrawer.getSectionsToRender().map((section) => section.id),
              sections_url: window.location.pathname,
            });

            this.cartDrawer.renderContents(cartData, { openDrawer: false });
            this.cartDrawer.publishCartUpdate?.(cartData, variantId);
            return cartData;
          });

          this.latestCartData = parsedState;
          this.addedVariantIds.add(variantId);
          this.markOfferAdded(button);
          this.setBusy(false);

          const allOffersAdded = this.addedVariantIds.size >= this.availableOfferCount;
          if (allOffersAdded) {
            this.finish();
            return;
          }

          this.setStatus(this.dataset.successText);
        } catch (error) {
          console.error(error);
          this.setBusy(false);
          if (this.dialog?.open) {
            this.setStatus(this.dataset.errorText, true);
          } else {
            this.cartDrawer.announce?.(this.dataset.errorText, true);
          }
        } finally {
          if (this.offerRequestTimeout) window.clearTimeout(this.offerRequestTimeout);
          this.offerRequestTimeout = null;
        }
      }

      markOfferAdded(button) {
        button.classList.add('is-added');
        button.setAttribute('data-finch-offer-added', 'true');
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-busy', 'false');

        const label = button.querySelector('[data-finch-offer-add-label]');
        if (label) label.textContent = this.dataset.addedLabel;
      }

      setBusy(busy, activeButton = null) {
        this.requestInFlight = busy;
        this.setAttribute('aria-busy', String(busy));

        this.offerButtons.forEach((button) => {
          const isAdded = button.hasAttribute('data-finch-offer-added');
          button.disabled = busy || isAdded;
          button.setAttribute('aria-disabled', String(busy || isAdded));
          button.setAttribute('aria-busy', String(busy && button === activeButton));
        });
      }

      setStatus(message, isError = false) {
        if (!this.statusElement) return;

        this.statusElement.textContent = message || '';
        this.statusElement.classList.toggle('is-error', Boolean(isError));
      }
    }
  );
}
